import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Globe, RefreshCw, Plus, Trash2, Edit2, Check, X, Search, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react'
import {
  getExportFees, syncExportFees,
  createManualExportFee, updateManualExportFee, deleteManualExportFee,
  getOrderCarriers, lookupOrderForExport,
} from '../lib/api'
import { useToast, ToastContainer } from './Toast'

function formatCurrency(v, currency = 'AUD') {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: currency || 'AUD',
    minimumFractionDigits: 2,
  }).format(v ?? 0)
}

const todayStr = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const currentMonthStart = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

// Map raw ShipStation carrier codes → friendly display names.
const CARRIER_LABELS = {
  dhl_express:          'DHL Express',
  dhl_ecommerce:        'DHL eCommerce',
  dhl_ecommerce_au:     'DHL eCommerce',
  fedex:                'FedEx',
  fedex_international:  'FedEx International',
  ups:                  'UPS',
  ups_walleted:         'UPS',
  tnt:                  'TNT',
  australia_post:       'Australia Post',
  auspost:              'Australia Post',
  startrack:            'StarTrack',
  startrack_express:    'StarTrack Express',
  toll:                 'Toll',
  couriersplease:       'Couriers Please',
  fastway:              'Fastway',
  aramex:               'Aramex',
  sendle:               'Sendle',
  allied:               'Allied Express',
  border_express:       'Border Express',
  usps:                 'USPS',
  royal_mail:           'Royal Mail',
  canada_post:          'Canada Post',
}

function formatCarrier(code) {
  if (!code) return code
  const key = code.toLowerCase().replace(/[^a-z0-9]/g, '_')
  if (CARRIER_LABELS[key]) return CARRIER_LABELS[key]
  // Fallback: title-case with spaces
  return code.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function buildCourierList(carriers = []) {
  // Deduplicate by friendly name — map code → label, then unique by label
  const seen = new Set()
  const result = []
  for (const code of carriers) {
    const label = formatCarrier(code)
    if (!seen.has(label)) {
      seen.add(label)
      result.push({ code, label })
    }
  }
  result.sort((a, b) => a.label.localeCompare(b.label))
  if (!seen.has('Other')) result.push({ code: 'Other', label: 'Other' })
  return result
}

const EMPTY_FORM = {
  order_number: '',
  tracking_number: '',
  fee_date: todayStr(),
  courier: 'DHL',
  currency: 'AUD',
  duty_amount: '',
  tax_amount: '',
  fee_amount: '',
  shipping_amount: '',
  notes: '',
}

// Source classification for an export fee row. Drives the badge in the
// "Source" column and the per-source subtotal in the footer row.
//   • manual=true                        → Manual (admin added by hand)
//   • courier='fedex' + no zonos_order_id → FedEx (auto-ingest from FedEx duty PDF)
//   • otherwise                          → Zonos (sync from Zonos)
function classifySource(fee) {
  if (fee.manual) return { key: 'manual', label: 'Manual', cls: 'bg-violet-100 text-violet-700' }
  if ((fee.courier || '').toLowerCase() === 'fedex' && !fee.zonos_order_id) {
    return { key: 'fedex', label: 'FedEx', cls: 'bg-orange-100 text-orange-700' }
  }
  return { key: 'zonos', label: 'Zonos', cls: 'bg-blue-100 text-blue-700' }
}

// ---------------------------------------------------------------------------
// Amount input helper
// ---------------------------------------------------------------------------
function AmountInput({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="0.00"
        className="input w-full text-sm text-right"
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Add form
// ---------------------------------------------------------------------------
function AddForm({ clientId, couriers, onSaved, onCancel }) {
  const qc = useQueryClient()
  const courierList = buildCourierList(couriers)
  const [form, setForm] = useState({ ...EMPTY_FORM, courier: courierList[0]?.label || '' })
  const [looking, setLooking] = useState(false)
  const [lookupErr, setLookupErr] = useState(null)

  const mutation = useMutation({
    mutationFn: (data) => createManualExportFee(clientId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['export-fees', clientId] })
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] })
      onSaved()
    },
  })

  const set = (field) => (val) => setForm(f => ({ ...f, [field]: val }))

  async function lookupByOrderNumber() {
    const val = form.order_number.trim()
    if (!val) return
    setLooking(true); setLookupErr(null)
    try {
      const result = await lookupOrderForExport(clientId, { order_number: val })
      setForm(f => ({
        ...f,
        order_number:    result.order_number    || f.order_number,
        tracking_number: result.tracking_number || f.tracking_number,
      }))
    } catch {
      setLookupErr('Order not found')
    } finally {
      setLooking(false)
    }
  }

  async function lookupByTracking() {
    const val = form.tracking_number.trim()
    if (!val) return
    setLooking(true); setLookupErr(null)
    try {
      const result = await lookupOrderForExport(clientId, { tracking_number: val })
      setForm(f => ({
        ...f,
        order_number:    result.order_number    || f.order_number,
        tracking_number: result.tracking_number || f.tracking_number,
      }))
    } catch {
      // no match — not an error, just leave blank
    } finally {
      setLooking(false)
    }
  }

  const total = ['duty_amount', 'tax_amount', 'fee_amount', 'shipping_amount']
    .reduce((s, k) => s + (parseFloat(form[k]) || 0), 0)

  function handleSave() {
    if (!form.fee_date || !form.courier) return
    mutation.mutate({
      order_number:    form.order_number.trim() || null,
      tracking_number: form.tracking_number.trim() || null,
      fee_date:        form.fee_date,
      courier:         form.courier,
      currency:        form.currency,
      duty_amount:     parseFloat(form.duty_amount) || 0,
      tax_amount:      parseFloat(form.tax_amount) || 0,
      fee_amount:      parseFloat(form.fee_amount) || 0,
      shipping_amount: parseFloat(form.shipping_amount) || 0,
      notes:           form.notes.trim() || null,
    })
  }

  return (
    <div className="card p-4 mb-4 border border-blue-200 bg-blue-50/40">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">Add Manual Export Fee</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-3">
        {/* Date */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Date <span className="text-red-400">*</span></label>
          <input type="date" value={form.fee_date} onChange={e => set('fee_date')(e.target.value)}
            className="input w-full text-sm" />
        </div>
        {/* Courier */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Courier <span className="text-red-400">*</span></label>
          <select value={form.courier} onChange={e => set('courier')(e.target.value)} className="input w-full text-sm">
            {courierList.map(({ label }) => <option key={label} value={label}>{label}</option>)}
          </select>
        </div>
        {/* Currency */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Currency</label>
          <select value={form.currency} onChange={e => set('currency')(e.target.value)} className="input w-full text-sm">
            {['AUD', 'USD', 'EUR', 'GBP'].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        {/* Order # — lookup on blur fills tracking */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">
            Order #
            {looking && <Loader2 size={10} className="inline animate-spin ml-1 text-slate-400" />}
            {lookupErr && <span className="ml-1 text-red-400">{lookupErr}</span>}
          </label>
          <input
            value={form.order_number}
            onChange={e => { set('order_number')(e.target.value); setLookupErr(null) }}
            onBlur={lookupByOrderNumber}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur() } }}
            placeholder="Optional"
            className="input w-full text-sm"
          />
        </div>
        {/* Tracking — lookup on blur fills order # */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">
            Tracking
            {looking && <Loader2 size={10} className="inline animate-spin ml-1 text-slate-400" />}
          </label>
          <input
            value={form.tracking_number}
            onChange={e => { set('tracking_number')(e.target.value); setLookupErr(null) }}
            onBlur={lookupByTracking}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur() } }}
            placeholder="Optional"
            className="input w-full text-sm"
          />
        </div>
        {/* Amounts */}
        <AmountInput label="Duty" value={form.duty_amount} onChange={set('duty_amount')} />
        <AmountInput label="Tax" value={form.tax_amount} onChange={set('tax_amount')} />
        <AmountInput label="Fees" value={form.fee_amount} onChange={set('fee_amount')} />
        <AmountInput label="Shipping" value={form.shipping_amount} onChange={set('shipping_amount')} />
        {/* Total (read only) */}
        <div>
          <label className="block text-xs text-slate-500 mb-1">Total</label>
          <div className="input w-full text-sm text-right font-semibold bg-slate-50 text-slate-700">
            {formatCurrency(total, form.currency)}
          </div>
        </div>
        {/* Notes — span full row */}
        <div className="col-span-2 sm:col-span-3">
          <label className="block text-xs text-slate-500 mb-1">Notes</label>
          <input value={form.notes} onChange={e => set('notes')(e.target.value)}
            placeholder="e.g. Invoice #1234, shipment description…"
            className="input w-full text-sm" />
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={handleSave} disabled={mutation.isPending || !form.fee_date || !form.courier}
          className="btn-primary text-sm px-4">
          {mutation.isPending && <Loader2 size={13} className="animate-spin" />}
          Save
        </button>
        <button onClick={onCancel} className="btn-secondary text-sm px-3">Cancel</button>
        {mutation.isError && <span className="text-xs text-red-500 self-center">Failed to save.</span>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline edit row
// ---------------------------------------------------------------------------
function EditRow({ fee, couriers, onSave, onCancel }) {
  const courierList = buildCourierList(couriers)
  const [form, setForm] = useState({
    order_number:    fee.order_number || '',
    tracking_number: fee.tracking_number || '',
    fee_date:        fee.fee_date || todayStr(),
    courier:         fee.courier || courierList[0]?.label || '',
    currency:        fee.currency || 'AUD',
    duty_amount:     String(fee.duty_amount ?? ''),
    tax_amount:      String(fee.tax_amount ?? ''),
    fee_amount:      String(fee.fee_amount ?? ''),
    shipping_amount: String(fee.shipping_amount ?? ''),
    notes:           fee.notes || '',
  })
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }))

  const total = ['duty_amount', 'tax_amount', 'fee_amount', 'shipping_amount']
    .reduce((s, k) => s + (parseFloat(form[k]) || 0), 0)

  function commit() {
    onSave({
      order_number:    form.order_number.trim() || null,
      tracking_number: form.tracking_number.trim() || null,
      fee_date:        form.fee_date,
      courier:         form.courier,
      currency:        form.currency,
      duty_amount:     parseFloat(form.duty_amount) || 0,
      tax_amount:      parseFloat(form.tax_amount) || 0,
      fee_amount:      parseFloat(form.fee_amount) || 0,
      shipping_amount: parseFloat(form.shipping_amount) || 0,
      notes:           form.notes.trim() || null,
    })
  }

  const inp = 'w-full text-xs rounded border border-slate-200 px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400'
  const numInp = inp + ' text-right'

  return (
    <tr className="bg-blue-50/60">
      <td className="px-2 py-1.5 text-xs text-slate-400">
        {fee.synced_at ? fee.synced_at.slice(0, 10) : '—'}
      </td>
      <td className="px-2 py-1.5">
        <input type="date" value={form.fee_date} onChange={set('fee_date')} className={inp} />
      </td>
      <td className="px-2 py-1.5">
        <input value={form.order_number} onChange={set('order_number')} placeholder="—" className={inp} />
      </td>
      <td className="px-2 py-1.5">
        <input value={form.tracking_number} onChange={set('tracking_number')} placeholder="—" className={inp} />
      </td>
      <td className="px-2 py-1.5">
        <select value={form.courier} onChange={set('courier')} className={inp}>
          {courierList.map(({ label }) => <option key={label} value={label}>{label}</option>)}
        </select>
      </td>
      <td className="px-2 py-1.5">
        <select value={form.currency} onChange={set('currency')} className={inp}>
          {['AUD', 'USD', 'EUR', 'GBP'].map(c => <option key={c}>{c}</option>)}
        </select>
      </td>
      <td className="px-2 py-1.5"><input type="number" value={form.duty_amount} onChange={set('duty_amount')} className={numInp} /></td>
      <td className="px-2 py-1.5"><input type="number" value={form.tax_amount} onChange={set('tax_amount')} className={numInp} /></td>
      <td className="px-2 py-1.5"><input type="number" value={form.fee_amount} onChange={set('fee_amount')} className={numInp} /></td>
      <td className="px-2 py-1.5"><input type="number" value={form.shipping_amount} onChange={set('shipping_amount')} className={numInp} /></td>
      <td className="px-2 py-1.5 text-right text-xs font-semibold">{formatCurrency(total, form.currency)}</td>
      <td className="px-2 py-1.5"><input value={form.notes} onChange={set('notes')} placeholder="—" className={inp} /></td>
      <td className="px-2 py-1.5">
        <div className="flex gap-1">
          <button onClick={commit} className="p-1 rounded text-green-600 hover:bg-green-50"><Check size={13} /></button>
          <button onClick={onCancel} className="p-1 rounded text-slate-400 hover:bg-slate-100"><X size={13} /></button>
        </div>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------
export default function ExportsTab({ clientId }) {
  const [fromDate, setFromDate] = useState(currentMonthStart())
  const [toDate, setToDate]     = useState(todayStr())
  const [syncFrom, setSyncFrom] = useState(currentMonthStart())
  const [syncTo, setSyncTo]     = useState(todayStr())
  const [addOpen, setAddOpen]   = useState(false)
  const [editId, setEditId]     = useState(null)
  const [search, setSearch]     = useState('')
  const [page, setPage]         = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const { toasts, addToast, removeToast } = useToast()
  const qc = useQueryClient()

  const params = {
    ...(fromDate && { from: fromDate }),
    ...(toDate   && { to:   toDate   }),
  }

  const { data: fees = [], isLoading, error: feesError } = useQuery({
    queryKey: ['export-fees', clientId, params],
    queryFn:  () => getExportFees(clientId, params),
    enabled:  !!clientId,
  })

  const { data: carriers = [] } = useQuery({
    queryKey: ['order-carriers', clientId],
    queryFn:  () => getOrderCarriers(clientId),
    enabled:  !!clientId,
    staleTime: 5 * 60 * 1000,
  })

  const syncMutation = useMutation({
    mutationFn: () => syncExportFees(syncFrom, syncTo),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['export-fees', clientId] })
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] })
      const msg = result.success
        ? `Sync complete — ${result.fetched} fetched, ${result.matched} matched, ${result.unmatched} unmatched, ${result.skipped} skipped.`
        : `Sync finished with errors: ${result.errors.join('; ')}`
      addToast({ type: result.success ? 'success' : 'error', message: msg })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Zonos sync failed.' }),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateManualExportFee(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['export-fees', clientId] })
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] })
      setEditId(null)
    },
    onError: () => addToast({ type: 'error', message: 'Failed to update fee.' }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteManualExportFee(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['export-fees', clientId] })
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] })
    },
    onError: () => addToast({ type: 'error', message: 'Failed to delete fee.' }),
  })

  function handleDelete(fee) {
    if (!window.confirm(`Delete manual export fee${fee.order_number ? ` for order ${fee.order_number}` : ''}?`)) return
    deleteMutation.mutate(fee.id)
  }

  const filteredFees = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return fees
    return fees.filter(f =>
      (f.order_number    || '').toLowerCase().includes(q) ||
      (f.tracking_number || '').toLowerCase().includes(q) ||
      (f.courier         || '').toLowerCase().includes(q) ||
      (f.notes           || '').toLowerCase().includes(q)
    )
  }, [fees, search])

  const totalAmount = filteredFees.reduce((s, f) => s + (f.total_amount || 0), 0)
  const primaryCurrency = filteredFees[0]?.currency || fees[0]?.currency || 'AUD'

  // Per-source subtotals — drives the breakdown chip row above the table total.
  // Lets the admin see at a glance how much of MTD exports is Zonos vs FedEx
  // duty vs manual without exporting to CSV.
  const sourceTotals = useMemo(() => {
    const out = { zonos: 0, fedex: 0, manual: 0 }
    for (const f of filteredFees) {
      const { key } = classifySource(f)
      out[key] += (f.total_amount || 0)
    }
    return out
  }, [filteredFees])

  // Pagination — slice the filtered list. Reset to page 1 whenever the
  // filter set or page size changes so we never end up on a now-empty page.
  const totalPages = Math.max(1, Math.ceil(filteredFees.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const pagedFees = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return filteredFees.slice(start, start + pageSize)
  }, [filteredFees, safePage, pageSize])

  // Reset to page 1 when filters/size change
  useEffect(() => { setPage(1) }, [search, fromDate, toDate, pageSize])

  return (
    <div className="space-y-4">

      {/* Sync card */}
      <div className="card p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Sync from Zonos</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Pulls duty &amp; tariff fee records from the Zonos account and matches them to orders by tracking number.
              Already-synced records are skipped — safe to re-run.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={syncFrom} onChange={e => setSyncFrom(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <span className="text-slate-400 text-xs">to</span>
            <input type="date" value={syncTo} onChange={e => setSyncTo(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <button onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending || !syncFrom || !syncTo}
              className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5">
              {syncMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              Sync
            </button>
          </div>
        </div>
      </div>

      {/* Add form */}
      {addOpen && (
        <AddForm
          clientId={clientId}
          couriers={carriers}
          onSaved={() => setAddOpen(false)}
          onCancel={() => setAddOpen(false)}
        />
      )}

      {/* Fees table */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 gap-3 flex-wrap">
          <div className="flex items-center gap-3 sm:shrink-0">
            <h3 className="text-sm font-semibold text-slate-700">Export Fees</h3>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Order #, tracking, courier…"
                className="text-xs border border-slate-200 rounded-md pl-7 pr-2 py-1 w-40 sm:w-56 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="btn-secondary text-xs py-1 px-2 flex items-center gap-1"
              >
                <X size={13} /> Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <span className="text-slate-400 text-xs">to</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <button onClick={() => { setAddOpen(true); setEditId(null) }} disabled={addOpen}
              className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5">
              <Plus size={13} /> Add Manual Fee
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Sync Date</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Order Date</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tracking</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Curr.</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Duty</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Tax</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Fees</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Shipping</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes</th>
                <th className="px-3 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>{Array.from({ length: 13 }).map((_, j) => (
                    <td key={j} className="px-3 py-3"><div className="skeleton h-4 rounded" /></td>
                  ))}</tr>
                ))
              ) : feesError ? (
                /* A failed fetch leaves `fees` on its `= []` default, which would
                   render the empty state below plus a $0.00 total — a dropped
                   request must not read as "no export fees to bill". */
                <tr>
                  <td colSpan={13} className="py-12 text-center">
                    <AlertTriangle size={28} className="mx-auto text-red-400 mb-2" />
                    <p className="text-red-600 text-sm font-medium">Couldn't load export fees</p>
                    <p className="text-red-500 text-xs mt-1">
                      This is a load failure, not an empty period — fees may exist but aren't shown, and no total is being calculated.
                    </p>
                    <p className="text-slate-400 text-xs mt-1 font-mono break-all">
                      {feesError?.response?.data?.detail || feesError?.message || 'Unknown error'}
                    </p>
                  </td>
                </tr>
              ) : filteredFees.length === 0 ? (
                <tr>
                  <td colSpan={13} className="py-12 text-center">
                    <Globe size={28} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-400 text-sm">
                      {search ? 'No results match your search' : 'No export fees in this period'}
                    </p>
                    <p className="text-slate-300 text-xs mt-1">
                      {search ? 'Try a different order number or tracking number' : 'Sync from Zonos or add a manual entry above'}
                    </p>
                  </td>
                </tr>
              ) : pagedFees.map((fee, idx) => (
                editId === fee.id ? (
                  <EditRow
                    key={fee.id}
                    fee={fee}
                    couriers={carriers}
                    onSave={(data) => updateMutation.mutate({ id: fee.id, data })}
                    onCancel={() => setEditId(null)}
                  />
                ) : (
                  <tr key={fee.id}
                    className={`hover:bg-slate-50 transition-colors ${!fee.order_id && !fee.manual ? 'bg-amber-50/40' : ''} ${idx % 2 === 1 ? 'bg-slate-50/30' : ''}`}
                    title={!fee.order_id && !fee.manual ? 'No matching portal order found' : undefined}>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{fee.synced_at ? fee.synced_at.slice(0, 10) : '—'}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{fee.fee_date || '—'}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-700">
                      {fee.order_number || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{fee.tracking_number || '—'}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {(() => {
                        const src = classifySource(fee)
                        return (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${src.cls}`}
                            title={
                              src.key === 'fedex' ? 'Imported from a FedEx duty PDF via the FedEx Reconciliation ingest.'
                              : src.key === 'manual' ? `Manually entered${fee.courier ? ` (${fee.courier})` : ''}`
                              : 'Synced from Zonos.'
                            }
                          >
                            {src.key === 'manual' && fee.courier ? `${src.label} · ${fee.courier}` : src.label}
                          </span>
                        )
                      })()}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-400 uppercase">{fee.currency || 'USD'}</td>
                    <td className="px-3 py-2.5 text-right text-xs text-slate-600">{formatCurrency(fee.duty_amount, fee.currency)}</td>
                    <td className="px-3 py-2.5 text-right text-xs text-slate-600">{formatCurrency(fee.tax_amount, fee.currency)}</td>
                    <td className="px-3 py-2.5 text-right text-xs text-slate-600">{formatCurrency(fee.fee_amount, fee.currency)}</td>
                    <td className="px-3 py-2.5 text-right text-xs text-slate-600">{formatCurrency(fee.shipping_amount, fee.currency)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-800 text-xs">{formatCurrency(fee.total_amount, fee.currency)}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-400">{fee.notes || '—'}</td>
                    <td className="px-3 py-2.5">
                      {fee.manual && (
                        <div className="flex gap-1">
                          <button onClick={() => setEditId(fee.id)}
                            className="p-1 rounded text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                            <Edit2 size={12} />
                          </button>
                          <button onClick={() => handleDelete(fee)} disabled={deleteMutation.isPending}
                            className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              ))}

              {filteredFees.length > 0 && (
                <>
                  {/* Per-source subtotals — only show the sources that have $$ */}
                  {(sourceTotals.zonos + sourceTotals.fedex + sourceTotals.manual) > 0 && (
                    <tr className="bg-slate-50/60 border-t border-slate-200">
                      <td colSpan={10} className="px-3 py-2 text-right text-xs text-slate-500">
                        <span className="inline-flex items-center gap-3 flex-wrap justify-end">
                          {sourceTotals.zonos > 0 && (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
                              <span>Zonos</span>
                              <span className="text-slate-700 tabular-nums">{formatCurrency(sourceTotals.zonos, primaryCurrency)}</span>
                            </span>
                          )}
                          {sourceTotals.fedex > 0 && (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="inline-block w-2 h-2 rounded-full bg-orange-400" />
                              <span>FedEx</span>
                              <span className="text-slate-700 tabular-nums">{formatCurrency(sourceTotals.fedex, primaryCurrency)}</span>
                            </span>
                          )}
                          {sourceTotals.manual > 0 && (
                            <span className="inline-flex items-center gap-1.5">
                              <span className="inline-block w-2 h-2 rounded-full bg-violet-400" />
                              <span>Manual</span>
                              <span className="text-slate-700 tabular-nums">{formatCurrency(sourceTotals.manual, primaryCurrency)}</span>
                            </span>
                          )}
                        </span>
                      </td>
                      <td colSpan={3} />
                    </tr>
                  )}
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td colSpan={10} className="px-3 py-3 text-right text-xs font-semibold text-slate-500">
                      Total ({filteredFees.length} record{filteredFees.length !== 1 ? 's' : ''}
                      {search && fees.length !== filteredFees.length ? ` of ${fees.length}` : ''})
                    </td>
                    <td className="px-3 py-3 text-right font-bold text-slate-800 text-sm">
                      {formatCurrency(totalAmount, primaryCurrency)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredFees.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
            <div className="flex items-center gap-3">
              <p className="text-xs text-slate-500">
                Showing {((safePage - 1) * pageSize) + 1}–{Math.min(safePage * pageSize, filteredFees.length)} of {filteredFees.length.toLocaleString()} record{filteredFees.length !== 1 ? 's' : ''}
              </p>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                {[25, 50, 100, 200].map(n => (
                  <option key={n} value={n}>{n} per page</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Page {safePage} of {totalPages}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>

                {(() => {
                  const delta = 2
                  const range = []
                  for (let i = Math.max(2, safePage - delta); i <= Math.min(totalPages - 1, safePage + delta); i++) range.push(i)
                  const pages = totalPages <= 1 ? [1] : [1]
                  if (totalPages > 1) {
                    if (range.length > 0 && range[0] > 2) pages.push('…left')
                    pages.push(...range)
                    if (range.length > 0 && range[range.length - 1] < totalPages - 1) pages.push('…right')
                    pages.push(totalPages)
                  }
                  return pages.map(p =>
                    typeof p === 'string'
                      ? <span key={p} className="text-slate-400 text-xs px-1">…</span>
                      : <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={[
                            'w-7 h-7 rounded text-xs font-medium transition-colors',
                            p === safePage ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100',
                          ].join(' ')}
                        >
                          {p}
                        </button>
                  )
                })()}

                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  )
}
